import { Alert, Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NotesIcon from '@mui/icons-material/Notes';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import RadioButtonCheckedOutlinedIcon from '@mui/icons-material/RadioButtonCheckedOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import PublicIcon from '@mui/icons-material/Public';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ConsoleHelper from 'src/shared/functions/consoleHelper';

import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getFormSettingFields, updateFormSettingField, FormSettingField } from 'src/shared/services/memberFormSettingsService';
import { WebFormSettingEditModal } from './webFormSettingEditModal';

type WebFormConfiguratorProps = {
  formName: string;
  isOtherMember?: boolean;
  readOnly?: boolean;
};

const typeIcons: Record<number, JSX.Element> = {
  1: <TextFieldsIcon fontSize='small' />,
  2: <NotesIcon fontSize='small' />,
  4: <ArrowDropDownCircleOutlinedIcon fontSize='small' />,
  6: <RadioButtonCheckedOutlinedIcon fontSize='small' />,
  7: <CheckBoxOutlinedIcon fontSize='small' />,
  10: <PublicIcon fontSize='small' />,
  14: <TextFieldsIcon fontSize='small' />,
  18: <VisibilityOffOutlinedIcon fontSize='small' />,
  21: <LabelOutlinedIcon fontSize='small' />,
  33: <ViewHeadlineIcon fontSize='small' />,
};

const typeLabels: Record<number, string> = {
  1: 'Text',
  2: 'Flerrad',
  3: 'Textblock',
  4: 'Dropdown',
  6: 'Radioknapp',
  7: 'Kryssruta',
  10: 'Landsväljare',
  12: 'Kommun',
  14: 'Postnr',
  15: 'Datum',
  18: 'Dolt fält',
  21: 'Etikett',
  33: 'Rubrik',
};

export const WebFormConfigurator = ({ formName, readOnly = false, isOtherMember = false }: WebFormConfiguratorProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const isMounted = useIsMounted();

  const [fields, setFields] = useState<FormSettingField[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<FormSettingField | null>(null);
  const [dragField, setDragField] = useState<FormSettingField | null>(null);

  const fetchFields = useCallback(async () => {
    try {
      const referenceFormName = isOtherMember ? 'other' : 'member';
      const [webFormRes, memberRes] = await Promise.all([getFormSettingFields(formName), getFormSettingFields(referenceFormName)]);
      if (isMounted()) {
        // Fält som är synliga i medlemsformuläret
        const memberVisibleNames = new Set(memberRes.data.filter((f: FormSettingField) => f.show).map((f: FormSettingField) => f.fieldName));
        // Filtrera webformfält: visa bara de som är synliga i medlemsformuläret (headline- och profilfält undantas)
        const isExempt = (name: string) =>
          name.startsWith('headline') || name.startsWith('text') || name.startsWith('profileSpecial') || name === 'profile' || name === 'profileHead';
        const filtered = webFormRes.data.filter((f: FormSettingField) => isExempt(f.fieldName) || memberVisibleNames.has(f.fieldName));
        setFields(filtered);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    }
  }, [formName, isOtherMember, isMounted]);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  const handleEdit = (field: FormSettingField) => {
    setEditingField(field);
    setEditModalOpen(true);
  };

  const handleModalSave = () => {
    setEditModalOpen(false);
    setEditingField(null);
    fetchFields();
  };

  const [dropTargetRow, setDropTargetRow] = useState<number | null>(null);

  const sortedFields = [...fields].sort((a, b) => a.row - b.row || a.formSettingsID - b.formSettingsID);

  // Flytta fält till en specifik rad och kolumn
  const handleDropOnRow = async (targetRow: number, targetCol?: number) => {
    if (!dragField) return;

    const col = targetCol ?? sortedFields.filter((f) => f.row === targetRow).length;

    if (dragField.row === targetRow && dragField.col === col) {
      setDragField(null);
      setDropTargetRow(null);
      return;
    }

    try {
      await updateFormSettingField(formName, dragField.formSettingsID, { ...dragField, row: targetRow, col });
      fetchFields();
    } catch (e) {
      ConsoleHelper.log(e);
    }
    setDragField(null);
    setDropTargetRow(null);
  };

  // Släpp på ett specifikt fält — byt plats
  const handleDropOnField = async (targetField: FormSettingField) => {
    if (!dragField || dragField.formSettingsID === targetField.formSettingsID) return;

    try {
      await Promise.all([
        updateFormSettingField(formName, dragField.formSettingsID, { ...dragField, row: targetField.row, col: targetField.col }),
        updateFormSettingField(formName, targetField.formSettingsID, { ...targetField, row: dragField.row, col: dragField.col }),
      ]);
      fetchFields();
    } catch (e) {
      ConsoleHelper.log(e);
    }
    setDragField(null);
    setDropTargetRow(null);
  };

  // Släpp på ny rad (under alla befintliga)
  const handleDropOnNewRow = async () => {
    if (!dragField) return;
    const maxRow = sortedFields.reduce((max, f) => Math.max(max, f.row), 0);
    await handleDropOnRow(maxRow + 1, 0);
  };

  const getTypeIcon = (field: FormSettingField) => {
    // Profilfält med type 1 renderas som checkbox (samma override som GetFormResponse för webform)
    const isProfileField = field.fieldName.startsWith('profileSpecial') || field.fieldName === 'profile' || field.fieldName === 'profileHead';
    const isHeadline = field.fieldName.startsWith('headline');
    const isTextField = field.fieldName.startsWith('text');
    let displayType = field.type;
    if (isProfileField && field.type === 1) displayType = 7;
    if (isHeadline) displayType = 33;
    if (isTextField) displayType = 3;
    const icon = typeIcons[displayType] || <TextFieldsIcon fontSize='small' />;
    return (
      <Tooltip title={typeLabels[displayType] || String(displayType)} arrow>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>{icon}</Box>
      </Tooltip>
    );
  };

  return (
    <Grid container spacing={2} sx={{ p: 3 }}>
      <Grid item xs={12} md={4}>
        <Typography variant='subtitle1' fontWeight='bold'>
          {t('settings:memberRegistry.fieldList')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {t('settings:memberRegistry.fieldListDescription')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {'- '}
          {t('webforms:fieldListHint1')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {'- '}
          {t('webforms:fieldListHint2')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {'- '}
          {t('webforms:fieldListHint3')}
        </Typography>
        <Alert severity='info' sx={{ mt: 2 }}>
          {t('webforms:fieldListAlert')}
        </Alert>
        {readOnly && (
          <Alert severity='warning' sx={{ mt: 1 }}>
            {t('webforms:readOnlyWarning')}
          </Alert>
        )}
      </Grid>
      <Grid item xs={12} md={8} sx={{ opacity: readOnly ? 0.6 : 1, pointerEvents: readOnly ? 'none' : 'auto' }}>
        {(() => {
          const visibleSorted = sortedFields.filter((f) => f.show);
          const hiddenSorted = sortedFields.filter((f) => !f.show);
          // Gruppera synliga fält per rad
          const fieldRows = visibleSorted.reduce((acc, field) => {
            const r = field.row;
            if (!acc[r]) acc[r] = [];
            acc[r].push(field);
            return acc;
          }, {} as Record<number, FormSettingField[]>);
          Object.values(fieldRows).forEach((rowFields) => rowFields.sort((a, b) => a.col - b.col));

          const isHeadlineBox = (f: FormSettingField) => f.fieldName.startsWith('headline');
          const hideIcon = (f: FormSettingField) => isHeadlineBox(f) || f.fieldName.startsWith('text');

          const renderFieldBox = (field: FormSettingField) => (
            <Box
              key={field.formSettingsID}
              draggable={!readOnly}
              onDragStart={(e) => {
                if (readOnly) return;
                setDragField(field);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => !readOnly && e.preventDefault()}
              onDrop={(e) => {
                if (readOnly) return;
                e.stopPropagation();
                handleDropOnField(field);
              }}
              display='flex'
              gap={0.5}
              px={1}
              py={0.5}
              alignItems='center'
              sx={{
                bgcolor: dragField?.formSettingsID === field.formSettingsID ? 'primary.50' : '#fff',
                border: 1,
                borderColor: 'primary.200',
                borderRadius: 1,
                opacity: field.show ? 1 : 0.5,
                minWidth: 0,
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicatorIcon fontSize='small' sx={{ color: 'text.disabled', mr: -0.5 }} />
              {!hideIcon(field) && getTypeIcon(field)}
              <Typography variant='body2' sx={{ flex: 1, minWidth: 0, fontWeight: isHeadlineBox(field) ? 600 : 400 }} noWrap>
                {field.heading || field.fieldName}
                {field.required && (
                  <Typography component='span' variant='body2' color='error.main' sx={{ fontWeight: 700, ml: 0.5 }}>
                    *
                  </Typography>
                )}
              </Typography>
              <IconButton size='small' onClick={() => handleEdit(field)} disabled={readOnly}>
                <EditOutlinedIcon fontSize='small' />
              </IconButton>
            </Box>
          );

          const rowNumbers = Object.keys(fieldRows)
            .map(Number)
            .sort((a, b) => a - b);

          return (
            <>
              {rowNumbers.map((rowNum) => {
                const rowFields = fieldRows[rowNum];
                return (
                  <Box
                    key={rowNum}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDropTargetRow(rowNum);
                    }}
                    onDragLeave={() => setDropTargetRow(null)}
                    onDrop={() => handleDropOnRow(rowNum)}
                    sx={{
                      mb: 0.5,
                      p: 0.25,
                      borderRadius: 1,
                      border: dropTargetRow === rowNum && dragField ? '2px dashed' : '2px solid transparent',
                      borderColor: dropTargetRow === rowNum && dragField ? 'primary.main' : 'transparent',
                    }}
                  >
                    <Grid container spacing={0.5}>
                      {rowFields.map((field) => (
                        <Grid item xs={12} key={field.formSettingsID}>
                          {renderFieldBox(field)}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              })}

              {/* Drop-zon för ny rad */}
              {dragField && (
                <Box
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDropTargetRow(-1);
                  }}
                  onDragLeave={() => setDropTargetRow(null)}
                  onDrop={handleDropOnNewRow}
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    borderRadius: 1,
                    border: '2px dashed',
                    borderColor: dropTargetRow === -1 ? 'primary.main' : 'grey.300',
                    bgcolor: dropTargetRow === -1 ? 'primary.50' : 'transparent',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    {t('settings:memberRegistry.dropNewRow')}
                  </Typography>
                </Box>
              )}

              {hiddenSorted.length > 0 && (
                <Typography variant='caption' color='text.secondary' sx={{ mt: 1, mb: 0.5, display: 'block' }}>
                  {t('settings:memberRegistry.hiddenFields')}
                </Typography>
              )}
              {hiddenSorted.map((field) => (
                <Box key={field.formSettingsID} sx={{ mb: 0.5 }}>
                  {renderFieldBox(field)}
                </Box>
              ))}
            </>
          );
        })()}
      </Grid>

      {/* Redigera-modal */}
      {editModalOpen && (
        <WebFormSettingEditModal
          formName={formName}
          field={editingField}
          usedFieldNames={fields.map((f) => f.fieldName)}
          onSave={handleModalSave}
          onClose={() => {
            setEditModalOpen(false);
            setEditingField(null);
          }}
        />
      )}
    </Grid>
  );
};
